import { checkTier, EventRFeature, TierTeam } from "@eventr/common";
import { ColoursObj, Icon } from "@eventr/common-storybook";
import { Box } from "@mui/material";
import { useShallow } from "zustand/react/shallow";
import { useBoundStore } from "state/bound.store";
import { useIsOwner } from "state/hooks/useIsOwner";
import { showOnlyOwnerCanDo } from "app/account/components/event-r-pro-button";
import { useDeviceInfo } from "feat/appState/deviceInfo";
import { paywallSupported, presentUpgradePaywall } from "../../revenueCat/upgrade";

interface WithFeaturePermissionProps {
  requiredFeature: EventRFeature;
  fallback?: React.ReactNode;
}

// Higher Order Component
export const withFeaturePermission = <P extends object>(
  Component: React.ComponentType<P>,
  options: WithFeaturePermissionProps,
) => {
  const WithFeaturePermission = (props: P) => {
    const { tier } = useBoundStore(
      useShallow((s) => ({
        tier: s.tier,
      })),
    );
    const isOwner = useIsOwner();
    // Subscribed, not read once: the device reports its version after the first render.
    const buildVersion = useDeviceInfo((s) => s.version.buildVersion);

    if (!paywallSupported(buildVersion)) return <Component {...props} />;
    const currentTier = tier || TierTeam.id;

    const hasPermission = checkTier(currentTier, options.requiredFeature);
    if (!hasPermission) {
      return (
        <Box
          sx={{ position: "relative" }}
          onClick={async (e: any) => {
            e.stopPropagation();
            e.preventDefault();
            if (!isOwner) {
              showOnlyOwnerCanDo();
              return;
            }
            await presentUpgradePaywall();
          }}
        >
          <Box>
            <Box
              sx={{
                borderRadius: "8px",
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backdropFilter: "blur(1px)", // Adjust blur amount as needed
                zIndex: 1, // Ensure it's on top of the component
              }}
            />
            <Box
              sx={{
                borderRadius: "8px",
                position: "absolute",
                top: 5,
                right: 5,
                zIndex: 1, // Ensure it's on top of the component
              }}
            >
              <Icon name="Locked" />
            </Box>
            <div
              style={{
                pointerEvents: "none",
                backgroundColor: ColoursObj.shades.shade3,
                opacity: 0.5,
                borderRadius: "8px",
              }}
            >
              <Component {...props} />
            </div>
          </Box>
        </Box>
      );
    }

    return <Component {...props} />;
  };

  // Set displayName for better debugging
  WithFeaturePermission.displayName = `WithFeaturePermission(${
    Component.displayName || Component.name || "Component"
  })`;

  return WithFeaturePermission;
};
